<script setup lang="ts">
import { EVENT_TYPE } from "~/constants";

export interface CardVideoCardProps {
  title?: string;
  badgeTitle?: string;
  imageUrl?: string;
  isPlaying?: boolean;
  isActive?: boolean;
  loading?: boolean;
  badgeIcon?: "clip" | "highlight";
}

const props = withDefaults(defineProps<CardVideoCardProps>(), {
  isPlaying: false,
  isActive: false,
});
</script>

<template>
  <div class="baseVideoCard__content flex justify-between" :class="{ active: isActive }">
    <div class="flex items-center">
      <div class="baseVideoCard__imageWrap">
        <BaseImage :src="props.imageUrl" :alt="props.imageUrl" class="baseVideoCard__image aspect-video" width="full" height="auto" />
        <div v-if="props.badgeIcon && props.badgeTitle" class="baseVideoCard__badge text-caption-medium flex items-center">
          <IconClip v-if="props.badgeIcon === EVENT_TYPE.CLIP" :width="8" :height="8" fill="var(--base-icon-color)" />
          <IconHighlight v-else-if="props.badgeIcon === EVENT_TYPE.HIGHLIGHT" :width="8" :height="8" fill="var(--base-icon-color)" />
          <span v-if="props.badgeTitle" class="px-1">{{ props.badgeTitle }}</span>
        </div>
        <div v-if="props.loading" class="baseVideoCard__loading text-caption-medium text-main flex h-full w-full">
          <IconLoadingSpinner :size="30" class="left-3 rtl:right-3" />
        </div>
      </div>

      <div class="flex items-center max-w-2 md:max-w-48">
        <div
          v-if="props.title"
          :title="props.title"
          :class="{ 'text-secondary': props.loading, 'text-main': !props.loading }"
          class="text-body-medium max-h-12 break-all line-clamp-2  mx-4"
        >
          <p>
            {{ props.title }}
          </p>
        </div>
      </div>
    </div>

    <IconEqualizer v-if="props.isPlaying" />
  </div>
</template>

<style lang="scss">
.baseVideoCard__content {
  @apply flex items-center py-2 px-4 cursor-pointer;

  background-color: var(--base-card-bgColor);

  &.active {
    background-color: var(--base-card-bgSecondaryColor);

    .baseVideoCard__imageWrap {
      border: solid 1px var(--base-error-color);
      border-radius: 4px;
    }
  }

  .baseVideoCard__imageWrap {
    @apply relative mb-0 flex-shrink-0 overflow-hidden;

    .baseVideoCard__image {
      @apply w-full object-cover min-h-[4,5rem] max-h-[5rem];

      border-radius: var(--base-border-radius);
    }

    .baseVideoCard__badge {
      @apply absolute bottom-0 left-0 px-1 z-[1] capitalize;

      color: var(--base-text-color);
      background-color: var(--base-card-bgSecondaryColor);
      border-top-right-radius: 4px;
      border-bottom-left-radius: 4px;
    }

    .baseVideoCard__loading {
      @apply absolute top-1/2 left-1/2
      flex justify-center items-center;

      transform: translate(-50%, -50%);

      &::before {
        @apply absolute w-full h-full opacity-75;

        content: '';
        background-color: var(--base-background-color);
      }

      @keyframes lds-spinner {
        0% {
          opacity: 1;
        }
        100% {
          opacity: 0;
        }
      }
    }
  }
}
</style>
